import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

interface SeoPage {
  title: string;
  description: string;
  keywords?: string[];
}

const SITE_URL = 'https://eastcoast.engineering';
const DEFAULT_IMAGE = `${SITE_URL}/assets/img/logos/logo-medium-square.png`;

const PAGE_SEO: Record<string, SeoPage> = {
  '/': {
    title: 'AI Agents for Business | East Coast Engineering',
    description: 'East Coast Engineering builds practical AI agents that handle real business work, then connects them to the data, tools and systems they need to operate reliably.',
    keywords: ['AI agents for business', 'business AI automation', 'AI agent development', 'AI engineering Tanzania', 'AI engineering Canada']
  },
  '/services': {
    title: 'AI Agents and Business Automation | East Coast Engineering',
    description: 'Build AI agents for real business workflows, supported by the data engineering, integrations, applications and operations that make them dependable.',
    keywords: ['AI agent development', 'business AI automation', 'AI engineering services', 'AI systems integration']
  },
  '/services/ai': {
    title: 'AI Agent Development for Business | East Coast Engineering',
    description: 'We design, build and operate AI agents for real business workflows, with secure tools, trusted data, human oversight, evaluation and production monitoring.'
  },
  '/services/web': {
    title: 'Web Application & API Development | East Coast Engineering',
    description: 'Secure, scalable web applications, backend platforms and APIs designed, built, modernised and maintained for growing organisations.'
  },
  '/services/mobile': {
    title: 'iOS & Android App Development | East Coast Engineering',
    description: 'Reliable cross-platform and native mobile applications backed by secure APIs, analytics, testing and long-term maintenance.'
  },
  '/services/devops': {
    title: 'Cloud, DevOps & Platform Engineering | East Coast Engineering',
    description: 'Cloud architecture, CI/CD, infrastructure automation, observability, security and reliability engineering that make software safer to operate and scale.'
  },
  '/whyus': {
    title: 'Why Hire East Coast Engineering | Tanzania & Canada',
    description: 'Work with senior software engineers who improve delivery speed, technical quality and lifetime cost while staying accountable for production outcomes.'
  },
  '/whyus/technical': {
    title: 'Software Quality, Architecture & Technical Improvement',
    description: 'Improve software architecture, code quality, security, performance, reliability and maintainability with experienced engineers in Tanzania and Canada.'
  },
  '/whyus/timeline': {
    title: 'Faster Software Delivery Without Sacrificing Quality',
    description: 'Reduce software delivery delays with clearer requirements, senior engineering, automated testing, CI/CD and visible risk management.'
  },
  '/whyus/cost': {
    title: 'Reduce Software Development & Cloud Operating Costs',
    description: 'Lower software lifetime cost through practical architecture, cloud cost optimisation, automation, maintainable code and the right engineering capacity.'
  },
  '/pricing/growth': {
    title: 'Dedicated Software Engineering Team | Growth Partner',
    description: 'Hire a dedicated remote software engineering team on a predictable monthly retainer. Continuous web, mobile, API, AI, DevOps, maintenance and technical leadership from Tanzania and Canada.',
    keywords: ['dedicated software engineering team', 'Software Development as a Service', 'software engineer as a service', 'software development contractor Tanzania', 'software development Canada', 'remote engineering team']
  },
  '/pricing/build': {
    title: 'On-Demand Software Engineering | Build as You Go',
    description: 'Flexible on-demand software development for features, fixes, integrations, DevOps and technical advice. Pay for the senior engineering capacity you use.'
  },
  '/pricing/launch': {
    title: 'Fixed-Price Software Development | Launch Package',
    description: 'A fixed-scope software development package for MVPs, internal tools and focused releases, covering discovery, engineering, testing, deployment and handover.'
  },
  '/pricing': {
    title: 'AI Agent Development Pricing & Engagement Models',
    description: 'Compare focused AI agent pilots, flexible engineering capacity and ongoing partnerships to build and operate AI capabilities for businesses in Tanzania, Canada and worldwide.',
    keywords: ['AI agent development pricing', 'AI engineering partnership', 'AI pilot', 'business AI automation']
  },
  '/stories': {
    title: 'Software Engineering Insights & Case Studies | East Coast Engineering',
    description: 'Practical articles and engineering stories about AI, MLOps, APIs, cloud cost, software architecture, product delivery and scaling technology businesses.'
  },
  '/process': {
    title: 'AI Agent Development Process | East Coast Engineering',
    description: 'See how we take a business workflow from discovery through agent feasibility, secure integrations, human oversight, production deployment and ongoing evaluation.'
  },
  '/process/discovery': {
    title: 'Software Discovery & Product Strategy Process',
    description: 'Our software discovery process clarifies users, business outcomes, constraints, integrations and technical risks before expensive development begins.'
  },
  '/process/requirements': {
    title: 'Software Requirements Gathering Process',
    description: 'How East Coast Engineering turns business workflows and customer needs into clear, testable software requirements and responsible delivery decisions.'
  },
  '/process/planning': {
    title: 'Software Project Planning Process',
    description: 'Our software planning process maps architecture, dependencies, delivery stages, risks, estimates and ownership before implementation begins.'
  },
  '/process/development-methodology': {
    title: 'Software Development Methodology & Delivery Cadence',
    description: 'A practical software development methodology shaped around business risk, feedback cadence, changing requirements and dependable incremental delivery.'
  },
  '/process/development': {
    title: 'Our Software Development & Engineering Process',
    description: 'See how our software engineers build secure, maintainable applications in visible increments using code review, CI, testing and frequent business feedback.'
  },
  '/process/testing': {
    title: 'Software Testing & Quality Assurance Process',
    description: 'Our software testing process covers functionality, integrations, performance, security, usability and release readiness before customers encounter defects.'
  },
  '/process/delivery': {
    title: 'Software Deployment, Delivery & Maintenance Process',
    description: 'Reliable software delivery with cloud infrastructure, automated deployment, observability, rollback controls, documentation and production ownership.'
  },
  '/process/monitoring': {
    title: 'Software Monitoring, Support & Continuous Improvement',
    description: 'How we monitor production software, respond to incidents, improve reliability and manage technical risk after launch.'
  },
  '/process/handover': {
    title: 'Software Handover, Documentation & Knowledge Transfer',
    description: 'A complete software handover process covering source code, cloud accounts, documentation, access, training, beta support and operational readiness.'
  },
  '/process/ownership': {
    title: 'Software Ownership, IP, Access & Source Code Control',
    description: 'Understand how we protect client ownership of source code, infrastructure, data, accounts, intellectual property and long-term operating independence.'
  },
  '/process/timeline': {
    title: 'Software Development Timelines & Estimates',
    description: 'A transparent guide to software estimates, delivery timelines, uncertainty, dependencies, trade-offs and the factors that change project schedules.'
  },
  '/process/need-to-know': {
    title: 'What to Know Before Starting Software Development',
    description: 'Practical expectations about scope, iteration, defects, decision-making, change and ownership before working with a software development contractor.'
  },
  '/industries': {
    title: 'Software Development by Industry | Tanzania & Canada',
    description: 'Custom software, AI, cloud and API engineering for startups, fintech, logistics, ecommerce and growing organisations in Tanzania, Canada and worldwide.'
  },
  '/industries/ecommerce': {
    title: 'Ecommerce Software Development & Integration Services',
    description: 'Custom ecommerce platforms, payment integrations, order systems, inventory automation and scalable APIs built for growing digital commerce operations.'
  },
  '/industries/fintech': {
    title: 'Fintech Software Development & API Engineering',
    description: 'Secure fintech software, payment integrations, financial APIs, reconciliation workflows, cloud infrastructure and compliance-ready engineering.'
  },
  '/industries/logistics': {
    title: 'Logistics Software, Tracking & Integration Development',
    description: 'Custom logistics software for dispatch, tracking, fleet operations, warehouse workflows, customer portals, automation and partner API integrations.'
  },
  '/industries/sme': {
    title: 'Custom Software Development for SMEs',
    description: 'Practical internal tools, workflow automation, system integrations, reporting and cloud software for growing small and medium-sized businesses.'
  },
  '/industries/startups': {
    title: 'Startup MVP & SaaS Software Development',
    description: 'Senior product and software engineering for startups building MVPs, SaaS platforms, APIs, AI products and scalable cloud infrastructure.'
  },
  '/industries/ai': {
    title: 'AI Agents & Business Workflow Automation',
    description: 'Put AI to work in real operations with practical agents, connected business data, secure integrations, human oversight and reliable production engineering.'
  },
  '/about/contact': {
    title: 'Contact Software Engineers in Tanzania & Canada',
    description: 'Contact East Coast Engineering in Dar es Salaam or Edmonton for software development, AI, API, DevOps and Software Development as a Service.'
  },
  '/about/founders': {
    title: 'Meet the Founders | East Coast Engineering',
    description: 'Meet the engineering and business leaders behind East Coast Engineering and learn how we approach long-term technology partnerships.'
  },
  '/about/faq': {
    title: 'Software Development FAQs | East Coast Engineering',
    description: 'Answers about working with East Coast Engineering, engagement models, delivery, ownership, maintenance, software costs and project timelines.'
  },
  '/about/contact/success': {
    title: 'Message Received | East Coast Engineering',
    description: 'Your message has been received by East Coast Engineering.'
  },
  '/bookcall': {
    title: 'Book a Software Discovery Call | East Coast Engineering',
    description: 'Book a no-pressure discovery call to discuss your software product, AI opportunity, API platform or engineering challenge.'
  }
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private router: Router,
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  init(): void {
    this.scheduleUpdate(this.normalizePath(this.router.url));
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.scheduleUpdate(this.normalizePath(event.urlAfterRedirects)));
  }

  private scheduleUpdate(path: string): void {
    this.update(path);
    // Child page components currently set legacy metadata during initialisation.
    // Reapplying on the next task makes the central route map authoritative in SSR and the browser.
    setTimeout(() => {
      if (this.normalizePath(this.router.url) === path) this.update(path);
    });
  }

  private update(path: string): void {
    const canonical = `${SITE_URL}${path === '/' ? '/' : path}`;

    if (path.startsWith('/post/')) {
      this.meta.updateTag({ property: 'og:url', content: canonical });
      this.meta.updateTag({ property: 'og:type', content: 'article' });
      this.setRobotsPolicy(path);
      this.setCanonical(canonical);
      return;
    }

    const page = PAGE_SEO[path] ?? this.fallbackPage(path);

    this.title.setTitle(page.title);
    this.meta.updateTag({ name: 'description', content: page.description });
    this.meta.updateTag({ property: 'og:title', content: page.title });
    this.meta.updateTag({ property: 'og:description', content: page.description });
    this.meta.updateTag({ name: 'twitter:title', content: page.title });
    this.meta.updateTag({ name: 'twitter:description', content: page.description });
    this.meta.updateTag({ name: 'keywords', content: (page.keywords ?? this.defaultKeywords()).join(', ') });

    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:type', content: path.startsWith('/post/') ? 'article' : 'website' });
    this.meta.updateTag({ property: 'og:image', content: DEFAULT_IMAGE });
    this.meta.updateTag({ name: 'twitter:image', content: DEFAULT_IMAGE });
    this.setRobotsPolicy(path);
    this.setCanonical(canonical);
    this.setPageStructuredData(path, page, canonical);
  }

  private setCanonical(url: string): void {
    const links = [...this.document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]')];
    let link = links.shift();
    links.forEach(duplicate => duplicate.remove());
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private normalizePath(url: string): string {
    const path = url.split(/[?#]/, 1)[0].replace(/\/$/, '');
    return path || '/';
  }

  private setRobotsPolicy(path: string): void {
    const hostname = isPlatformBrowser(this.platformId) ? this.document.location.hostname : '';
    const privateUtilityPage = path === '/about/contact/success' || path === '/bookcall';
    const content = hostname === 'dev.eastcoast.engineering' || privateUtilityPage
      ? 'noindex, nofollow, noarchive'
      : 'index, follow, max-image-preview:large';
    this.meta.updateTag({ name: 'robots', content });
    this.meta.updateTag({ name: 'googlebot', content });
  }

  private fallbackPage(path: string): SeoPage {
    const label = path.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') ?? 'Software Engineering';
    const readableLabel = label.replace(/\b\w/g, character => character.toUpperCase());
    return {
      title: `${readableLabel} | East Coast Engineering`,
      description: `Explore ${label} expertise from East Coast Engineering, a Software Development as a Service company serving Tanzania, Canada and remote clients worldwide.`
    };
  }

  private defaultKeywords(): string[] {
    return [
      'software development Tanzania',
      'software development Canada',
      'software development Dar es Salaam',
      'software development Edmonton',
      'Software Development as a Service',
      'software engineer as a service',
      'software development contractor',
      'DevOps engineers',
      'AI engineering services'
    ];
  }

  private setPageStructuredData(path: string, page: SeoPage, canonical: string): void {
    let script = this.document.head.querySelector<HTMLScriptElement>('#ece-page-schema');
    if (!script) {
      script = this.document.createElement('script');
      script.id = 'ece-page-schema';
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    const routeSegments = path.split('/').filter(Boolean);
    const breadcrumbs = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      ...routeSegments.map((segment, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: segment.replace(/-/g, ' ').replace(/\b\w/g, character => character.toUpperCase()),
        item: `${SITE_URL}/${routeSegments.slice(0, index + 1).join('/')}`
      }))
    ];
    const isService = path.startsWith('/services/') || path.startsWith('/pricing/');
    const pageType = path === '/about/contact' ? 'ContactPage'
      : ['/stories', '/industries', '/services', '/pricing', '/process'].includes(path) ? 'CollectionPage'
      : 'WebPage';
    const graph: Record<string, unknown>[] = [{
      '@type': pageType,
      '@id': `${canonical}#webpage`,
      name: page.title,
      headline: page.title,
      description: page.description,
      url: canonical,
      inLanguage: 'en',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      breadcrumb: { '@id': `${canonical}#breadcrumb` }
    }, {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: breadcrumbs
    }];

    if (isService) {
      graph.push({
        '@type': 'Service',
        '@id': `${canonical}#service`,
        name: page.title,
        description: page.description,
        url: canonical,
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: ['Tanzania', 'Canada', 'Worldwide'],
        serviceType: path.startsWith('/pricing/') ? 'Software Development Engagement' : 'Software Engineering'
      });
    }

    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
  }
}
